import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { useCart } from '../../hooks/useCart'
import souvenirService, { ShippingQuote } from '../../services/souvenir.service'
import { ADDRESS_STATE_NAMES } from '../../constants/address-states'
import { useAuth } from '../../hooks/useAuth'
import { PhoneInput } from '../../components/ui'
import {
  TruckIcon,
  ShoppingBagIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

const CheckoutPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { items, subtotal, vendorName, clear } = useCart()

  const [deliveryMethod, setDeliveryMethod] = useState<'DELIVERY' | 'PICKUP'>('DELIVERY')
  const [address, setAddress] = useState({
    name: '',
    phone: '',
    address: '',
    city: '',
    state: 'WP Kuala Lumpur',
    postalCode: '',
  })
  const [quotes, setQuotes] = useState<ShippingQuote[]>([])
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null)
  const [quotesLoading, setQuotesLoading] = useState(false)
  const [placing, setPlacing] = useState(false)

  useEffect(() => {
    if (user) {
      setAddress((a) => ({
        ...a,
        name: a.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        phone: a.phone || user.phone || '',
      }))
    }
  }, [user])

  const addressComplete =
    address.name.trim() &&
    address.phone.trim() &&
    address.address.trim() &&
    address.city.trim() &&
    address.state &&
    address.postalCode.trim()

  const shippingCost = useMemo(() => {
    if (deliveryMethod === 'PICKUP') return 0
    const chosen = quotes.find((q) => q.serviceId === selectedServiceId)
    return chosen ? chosen.price : 0
  }, [deliveryMethod, quotes, selectedServiceId])

  const platformFee = subtotal * 0.15
  const total = subtotal + shippingCost + platformFee

  const fetchQuotes = async () => {
    if (!addressComplete) {
      toast.error(t('checkout.completeAddressFirst'))
      return
    }
    setQuotesLoading(true)
    setSelectedServiceId(null)
    try {
      const res = await souvenirService.getShippingQuotes(
        items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
        {
          address: address.address,
          city: address.city,
          state: address.state,
          postalCode: address.postalCode,
        },
      )
      const rates = res.data?.data || []
      setQuotes(rates)
      if (!rates.length) {
        toast.error(t('checkout.noRates'))
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('checkout.quotesFailed'))
    } finally {
      setQuotesLoading(false)
    }
  }

  const handlePlaceOrder = async () => {
    if (deliveryMethod === 'DELIVERY') {
      if (!addressComplete) {
        toast.error(t('checkout.completeAddressFirst'))
        return
      }
      if (!selectedServiceId) {
        toast.error(t('checkout.selectCourier'))
        return
      }
    }

    setPlacing(true)
    try {
      const orderRes = await souvenirService.createOrder({
        items: items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
        deliveryMethod,
        deliveryAddress: deliveryMethod === 'DELIVERY' ? address : undefined,
        courierServiceId: deliveryMethod === 'DELIVERY' ? selectedServiceId! : undefined,
      })
      const order = orderRes.data?.data

      // Pay from wallet
      await souvenirService.payOrder(order.id)

      clear()
      toast.success(t('checkout.orderPlaced'))
      navigate(`/my-orders/${order.id}`)
    } catch (err: any) {
      const msg = err.response?.data?.message
      // Rate expired server-side → refresh quotes for re-selection
      if (err.response?.status === 409) {
        toast.error(msg || t('checkout.rateExpired'))
        await fetchQuotes()
      } else {
        toast.error(msg || t('checkout.placeFailed'))
      }
    } finally {
      setPlacing(false)
    }
  }

  if (!items.length) {
    return (
      <div className="max-w-3xl mx-auto text-center py-16">
        <ShoppingBagIcon className="h-16 w-16 mx-auto text-gray-300" />
        <h2 className="mt-4 text-xl font-semibold text-gray-900">{t('cart.empty')}</h2>
        <button
          onClick={() => navigate('/products')}
          className="mt-6 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          {t('cart.continueShopping')}
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('checkout.title')}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Delivery method */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('checkout.deliveryMethod')}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setDeliveryMethod('DELIVERY')}
                className={`flex items-center gap-3 p-4 border rounded-lg text-left ${
                  deliveryMethod === 'DELIVERY'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <TruckIcon className="h-6 w-6 text-primary-600" />
                <div>
                  <p className="font-medium text-gray-900">{t('checkout.delivery')}</p>
                  <p className="text-xs text-gray-500">{t('checkout.deliveryHint')}</p>
                </div>
              </button>
              <button
                onClick={() => setDeliveryMethod('PICKUP')}
                className={`flex items-center gap-3 p-4 border rounded-lg text-left ${
                  deliveryMethod === 'PICKUP'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <ShoppingBagIcon className="h-6 w-6 text-primary-600" />
                <div>
                  <p className="font-medium text-gray-900">{t('checkout.pickup')}</p>
                  <p className="text-xs text-gray-500">{t('checkout.pickupHint')}</p>
                </div>
              </button>
            </div>
          </div>

          {/* Address */}
          {deliveryMethod === 'DELIVERY' && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('checkout.deliveryAddress')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('checkout.fullName')}
                  </label>
                  <input
                    value={address.name}
                    onChange={(e) => setAddress({ ...address, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <PhoneInput
                    label={t('checkout.phone')}
                    value={address.phone}
                    onChange={(value) => setAddress({ ...address, phone: value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('checkout.address')}
                  </label>
                  <input
                    value={address.address}
                    onChange={(e) => setAddress({ ...address, address: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('checkout.city')}
                  </label>
                  <input
                    value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('checkout.state')}
                  </label>
                  <select
                    value={address.state}
                    onChange={(e) => setAddress({ ...address, state: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {ADDRESS_STATE_NAMES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('checkout.postcode')}
                  </label>
                  <input
                    value={address.postalCode}
                    onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <button
                onClick={fetchQuotes}
                disabled={quotesLoading || !addressComplete}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-primary-600 text-primary-600 rounded-lg font-medium hover:bg-primary-50 disabled:opacity-50"
              >
                {quotesLoading ? (
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                ) : (
                  <TruckIcon className="h-5 w-5" />
                )}
                {t('checkout.getRates')}
              </button>
            </div>
          )}

          {/* Courier selection */}
          {deliveryMethod === 'DELIVERY' && quotes.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('checkout.courierOptions')}
              </h3>
              <div className="space-y-3">
                {quotes.map((q) => (
                  <label
                    key={q.serviceId}
                    className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer ${
                      selectedServiceId === q.serviceId
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="courier"
                      checked={selectedServiceId === q.serviceId}
                      onChange={() => setSelectedServiceId(q.serviceId)}
                      className="h-4 w-4 text-primary-600"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{q.courierName}</p>
                      <p className="text-sm text-gray-500">{q.serviceName}</p>
                    </div>
                    {q.eta && (
                      <span className="text-sm text-gray-500">{q.eta}</span>
                    )}
                    <span className="font-semibold text-gray-900">
                      RM{q.price.toFixed(2)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="bg-white shadow rounded-lg p-6 h-fit">
          <h3 className="text-lg font-medium text-gray-900">{t('cart.summary')}</h3>
          <p className="mt-1 text-xs text-gray-500">
            {t('cart.soldBy')}: {vendorName}
          </p>

          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">{t('cart.subtotal')}</span>
              <span className="font-medium">RM{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('checkout.shippingFee')}</span>
              <span className="font-medium">
                {deliveryMethod === 'PICKUP'
                  ? '—'
                  : shippingCost
                    ? `RM${shippingCost.toFixed(2)}`
                    : t('checkout.selectCourier')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('checkout.platformFee')}</span>
              <span className="font-medium">RM{platformFee.toFixed(2)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between text-base">
              <span className="font-semibold text-gray-900">{t('checkout.total')}</span>
              <span className="font-bold text-gray-900">RM{total.toFixed(2)}</span>
            </div>
          </div>

          <button
            onClick={handlePlaceOrder}
            disabled={
              placing ||
              (deliveryMethod === 'DELIVERY' && !selectedServiceId)
            }
            className="mt-6 w-full px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {placing
              ? t('checkout.placing')
              : `${t('checkout.payWithWallet')} — RM${total.toFixed(2)}`}
          </button>
          <p className="mt-3 text-xs text-gray-400 text-center">
            {t('checkout.walletHint')}
          </p>
        </div>
      </div>
    </div>
  )
}

export default CheckoutPage
